import React, { useState, useEffect, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBlogs } from "@/services/common/blogs";
import "@/styles/pages/blogDetails.scss";
import CardComponent from "@/components/card";
import Container from "@/components/container/Container";
import { Divider } from "antd";
import slugify from "slugify";

function sanitizeBlogHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const images = doc.querySelectorAll("img");

  images.forEach((img) => {
    // Fallback alt text
    if (!img.hasAttribute("alt") || img.getAttribute("alt")?.trim() === "") {
      img.setAttribute("alt", "Blog image");
    }

    // Fallback image if source fails
    img.setAttribute(
      "onerror",
      "this.onerror=null;this.src='https://placehold.co/600x400?text=Image+Not+Available';"
    );
  });

  return doc.body.innerHTML;
}

const BlogDetails = () => {
  const { slug, id } = useParams();
  const blogId = Number(id);

  // console.log("Blog ID:", id, param);
  // console.log("Blog Slug:", slug, param);

  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [blogIndex, setBlogIndex] = useState(null);

  useEffect(() => {
    async function fetchBlogs() {
      try {
        // fetch *all* blogs
        const all = await getBlogs();
        // keep only those with exactly one section
        const filtered = all.filter((b) => Array.isArray(b.contents) && b.contents.length === 1);
        setBlogs(filtered);

        // console.log("Filtered blogs:", filtered);

        // find matching by id or slug
        // console.log({ pid, id });
        const idx = filtered.findIndex(
          (b) => (blogId !== null && b.id === blogId) || (slug && b.slug === slug)
        );

        setBlogIndex(idx !== -1 ? idx : null);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      }
    }
    fetchBlogs();
  }, [slug, id]);

  // wait until we know where we are
  if (blogIndex === null) {
    return <div className="blog-details">{/* <Container>Loading...</Container> */}</div>;
  }

  const blog = blogs[blogIndex];
  const hasPrev = blogIndex > 0;
  const hasNext = blogIndex < blogs.length - 1;

  // safe labels
  const prevLabel = "← Previous Blog";
  let nextLabel = "Next →";
  if (hasNext) {
    const nextTitle = blogs[blogIndex + 1].title;
    nextLabel =
      nextTitle.length > 30 ? `Next: ${nextTitle.slice(0, 30)}... →` : `Next: ${nextTitle} →`;
  }

  const goToPrevBlog = () => {
    if (!hasPrev) return;
    const prev = blogs[blogIndex - 1];
    const prevAuthor = slugify(prev.authorName, {
      lower: true,
      strict: true,
      replacement: "-",
      remove: /[^\w\s-]/g,
    });
    navigate(`/blog/${prevAuthor}/${prev.slug}/${prev.id}`);
    setBlogIndex(blogIndex - 1);
  };

  const goToNextBlog = () => {
    if (!hasNext) return;
    const next = blogs[blogIndex + 1];
    const nextAuthor = slugify(next.authorName, {
      lower: true,
      strict: true,
      replacement: "-",
      remove: /[^\w\s-]/g,
    });
    navigate(`/blog/${nextAuthor}/${next.slug}/${next.id}`);
    setBlogIndex(blogIndex + 1);
  };

  return (
    <Fragment>
      <div className="blog-details">
        <Container>
          <CardComponent>
            <div style={{ maxWidth: "1000px", margin: "auto" }}>
              {/* Desktop nav */}
              <div className="desktop-nav">
                <span
                  className="prev-btn"
                  style={{
                    cursor: hasPrev ? "pointer" : "not-allowed",
                    color: hasPrev ? undefined : "#ccc",
                  }}
                  onClick={goToPrevBlog}
                >
                  {prevLabel}
                </span>
                <span
                  className="next-btn"
                  style={{
                    cursor: hasNext ? "pointer" : "not-allowed",
                    color: hasNext ? undefined : "#ccc",
                  }}
                  onClick={goToNextBlog}
                >
                  {nextLabel}
                </span>
              </div>

              {/* Mobile nav */}
              <div className="mobile-nav">
                <span
                  className="prev-btn"
                  style={{
                    cursor: hasPrev ? "pointer" : "not-allowed",
                    color: hasPrev ? undefined : "#ccc",
                  }}
                  onClick={goToPrevBlog}
                >
                  ← Previous
                </span>
                <span
                  className="next-btn"
                  style={{
                    cursor: hasNext ? "pointer" : "not-allowed",
                    color: hasNext ? undefined : "#ccc",
                  }}
                  onClick={goToNextBlog}
                >
                  Next →
                </span>
              </div>

              {/* Blog content */}
              <div className="blog">
                {/* Meta */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  <span>{new Date(blog.updatedAt).toLocaleDateString()}</span>
                  <span>{blog.readingTime || 0} min read</span>
                </div>
                <Divider style={{ margin: "5px 0" }} />

                {/* Title */}
                <h1
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    margin: "10px 0",
                  }}
                >
                  {blog.title}
                </h1>

                {/* Cover Image */}
                {blog.coverImages && blog.coverImages[0] && (
                  <img
                    className="blog-cover"
                    src={blog.coverImages[0]}
                    alt="Blog Cover"
                    style={{
                      width: "100%",
                      maxHeight: "500px",
                      objectFit: "contain",
                      display: "block",
                      marginBottom: "20px",
                      backgroundColor: "#f0f0f0",
                      borderRadius: "8px",
                    }}
                  />
                )}

                {/* Single section content */}
                <div
                  style={{
                    fontSize: "16px",
                    color: "#333",
                    lineHeight: "1.6",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeBlogHtml(blog.contents[0].sectionContent),
                  }}
                />

                {/* Author & Keywords */}
                <Divider style={{ margin: "20px 0" }} />
                {blog.authorName && (
                  <div>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Author:
                    </span>
                    <span style={{ marginLeft: "5px" }}>{blog.authorName}</span>
                  </div>
                )}
                {blog.keywords && blog.keywords.length > 0 && (
                  <div style={{ marginTop: "8px" }}>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Keywords:
                    </span>
                    {blog.keywords.map((kw, i) => (
                      <span key={i} style={{ marginLeft: "5px" }}>
                        {kw}
                        {i < blog.keywords.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default BlogDetails;
